import React, { useEffect, useMemo, useRef, useState } from "react";

// import { Layout, Menu, Button, theme } from "antd";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { io, Socket } from "socket.io-client";


import Navbar from "./components/Navbars/Navbar";
import Sidebar from "./components/Navbars/Sidebar";
import { ToastContainer, toast } from "react-toastify";
import { defaultAppToastContainerProps } from "./config/reactToastifyDefaults";
import { useDispatch, useSelector } from "react-redux";
import { RootState, ownerSelector } from "./store/store";
import {
  setCampaigns,
  setCurrentWorkSpace,
  setListId,
  setLists,
  setOwner,
  setUser,
  setTags,
} from "./store/slices/ownerSlice";
import {
  fetchTeamsData,
  fetchCampaigns,
  fetchUserBilling,
  fetchLists,
  fetchOwner,
  fetchTags,
  fetchTemplates,
  fetchUserOwnersWithWorkspaces,
} from "./utils";
import { refreshInboxUnreadChatsCount } from "./utils/inboxUnread";
import {
  setSelectedTemplate,
  setTemplates,
} from "./store/slices/templateSlice";
import { updateTeam } from "./store/slices/teamsSlice";
import { setSelectedCampaign } from "./store/slices/campaignSlice";
import {
  getAllConnections,
  getAllRequests,
  getProfileAnalytics,
  getQueuedActions,
} from "./store/features/dashboardActions";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { List } from "./types";
import {
  SOCKET_URL,
  SOCKET_IO_CLIENT_OPTIONS,
  PAYMENT_URL,
  BOOK_DEMO_CALL_URL,
} from "./constants";
import FreeTrialModal from "./components/Modals/FreeTrialModal";
import PaymentFailedModal from "./components/Modals/PaymentFailedModal";
import ExpiredPlanModal from "./components/Modals/ExpiredPlanModal";
import EmailRequiredModal from "./components/Modals/EmailRequiredModal";
import { refreshBillingSession } from "./components/Billings/billingSession";
import { updateOwner } from "./store/features/ownerActions";
import { updateUserProfile } from "./store/features/ownerActions";
import { resolveBillingSource } from "./utils/billingSource";
import { trackLinkedInRegistrationConversionIfNeeded } from "./utils/linkedinRegistrationConversion";
import {
  fetchPricingCatalog,
  isLaunchFreeBillingCatalog,
} from "./utils/pricingCatalog";
import TawkLiveChat from "./components/TawkLiveChat";

// Set to true to show free-trial popup for eligible users
const SHOW_TRIAL_POPUP = true;

const DefaultLayout: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const hasUserEmail = (currentUser: any) =>
    typeof currentUser?.email === "string" && currentUser.email.trim().length > 0;
  const hasNoPlan = (owner: any) =>
    !owner || !Array.isArray(owner.planhistory) || owner.planhistory.length === 0;
  const hasFreePlanOnly = (owner: any) => {
    const ph = owner?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    const last = ph[ph.length - 1];
    return last?.planName === "Free Plan";
  };
  const hasUsedTrial = (owner: any) => {
    const ph = owner?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    return ph.some((p: any) => p?.isTrial === true);
  };
  const hasActivePlan = (owner: any) => {
    const ph = owner?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    const last = ph[ph.length - 1];
    if (!last) return false;

    const now = new Date();

    // For trial entries, treat as active until trialEndDate/periodEndDate in the future.
    if (last.isTrial) {
      const endRaw = last.periodEndDate || last.trialEndDate;
      if (!endRaw) return true;
      const end = new Date(endRaw);
      if (!Number.isNaN(end.getTime()) && end < now) return false;
      return true;
    }

    // For paid plans, active while isActive is true or periodEndDate is in the future.
    const isActiveFlag = Boolean(last.isActive);
    const isCancelled = Boolean(last.isCancelled);
    const periodEndRaw = last.periodEndDate;

    if (!isActiveFlag && !periodEndRaw) return false;

    if (periodEndRaw) {
      const end = new Date(periodEndRaw);
      if (!Number.isNaN(end.getTime()) && end < now) return false;
      return true;
    }

    if (isCancelled) return false;
    return isActiveFlag;
  };
  const hasPaymentFailed = (owner: any) => {
    const tries = Number(owner?.PaymentTries ?? 0);
    const ph = owner?.planhistory;
    const last = Array.isArray(ph) && ph.length > 0 ? ph[ph.length - 1] : null;
    return (
      tries >= 1 &&
      last?.planId &&
      last.planId !== "FREE_PLAN" &&
      last?.isActive === false &&
      !Boolean(last?.isCancelled)
    );
  };
  const shouldShowTrialPopup = (billing: any) =>
    (hasNoPlan(billing) || hasFreePlanOnly(billing)) && !hasUsedTrial(billing);
  const shouldShowExpiredPlanPopup = (billing: any) =>
    hasUsedTrial(billing) && !hasActivePlan(billing) && !hasPaymentFailed(billing);
  // Plan gating (trial/expired prompts) applies within campaign areas,
  // but LinkedIn owner linking is only required for certain flows.
  const isPlanGatedArea =
    pathname.startsWith("/campaigns") || pathname.startsWith("/campaign-management");
  const isOwnerRequiredArea =
    pathname.startsWith("/campaigns") || pathname.startsWith("/campaign-management/create");
  const isOnBilling =
    pathname.startsWith("/billings") ||
    pathname.startsWith("/settings/billing");
  const isOnSettings = pathname.startsWith("/settings");
  // Local billing bypass removed: always enforce plan gating behavior.
  const isLocalBillingBypass = false;
  // const [collapsed, setCollapsed] = useState(false);
  // const [pathName, setPathName] = useState(window.location.pathname);

  
  const [linkedinCredentials, setLinkedinCredentials] = useState<{ email: string; password: string }>({
    email: "",
    password: "",
  });
  const [chanllengeIframeUrl, setChallengeIframeUrl] = useState<string>("");
  const [showLinkedInLogin, setShowLinkedInLogin] = useState(false);
  const [linkedinLoading] = useState(false);
  const [linkedinStage, setLinkedinStage] = useState<string>("login");
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [hasAutoShownTrialOnSignin, setHasAutoShownTrialOnSignin] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showEmailRequiredModal, setShowEmailRequiredModal] = useState(false);
  const [isResubscribing, setIsResubscribing] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isLaunchFreeBilling, setIsLaunchFreeBilling] = useState(false);
  const [pricingCatalog, setPricingCatalog] = useState<Awaited<
    ReturnType<typeof fetchPricingCatalog>
  > | null>(null);

  useEffect(() => {
    fetchPricingCatalog(PAYMENT_URL)
      .then((catalog) => {
        setPricingCatalog(catalog);
        setIsLaunchFreeBilling(isLaunchFreeBillingCatalog(catalog));
      })
      .catch(() => {
        setPricingCatalog(null);
        setIsLaunchFreeBilling(false);
      });
  }, []);

  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const billingSource = useMemo(
    () => resolveBillingSource({ owner, currentUser }),
    [owner, currentUser]
  );
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const navigate = useNavigate();
  const hasShownOwnerRequiredToastRef = useRef(false);
  const initializedOwnerIdRef = useRef<string | null>(null);
  const hasAutoShownEmailRequiredRef = useRef(false);
  const hydratedBillingUserIdRef = useRef<string>("");
  const billingHydrationInFlightRef = useRef(false);
  const linkedInSocketRef = useRef<Socket | null>(null);
  const checkedLinkedinRegistrationConversionUserIdRef = useRef<string>("");

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const hydrateUserBilling = async () => {
      const userId =
        (typeof currentUser?.userId === "string" && currentUser.userId) ||
        (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
        "";
      if (!userId) return;
      if (billingHydrationInFlightRef.current) return;
      if (hydratedBillingUserIdRef.current === userId) return;
      try {
        billingHydrationInFlightRef.current = true;
        const billingData = await fetchUserBilling({ userId });
        if (!billingData?.userBilling) {
          // Still mark hydrated so we do not loop on permanent fetch failures.
          hydratedBillingUserIdRef.current = userId;
          return;
        }
        hydratedBillingUserIdRef.current = userId;
        dispatch(
          setUser({
            ...(currentUser || {}),
            userId: billingData?.userId || userId,
            email:
              billingData?.email ||
              currentUser?.email ||
              owner?.email ||
              "",
            userBilling: billingData.userBilling,
          })
        );
      } catch (_) {
        // Best-effort hydration only.
      } finally {
        billingHydrationInFlightRef.current = false;
      }
    };
    void hydrateUserBilling();
  }, [
    currentUser?.userId,
    owner?._id,
    (owner as any)?.userId,
    owner?.email,
    currentUser?.email,
    dispatch,
  ]);

  // After Safepay (or any external tab), re-pull billing so trial/plan CTAs update without re-login.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const userId =
        (typeof currentUser?.userId === "string" && currentUser.userId) ||
        (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
        "";
      if (!userId) return;
      hydratedBillingUserIdRef.current = "";
      refreshBillingSession({ owner, currentUser, dispatch }).catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [currentUser?.userId, owner?._id, (owner as any)?.userId, owner, currentUser, dispatch]);

  useEffect(() => {
    // Global email prompt (revamped modal). Show once per app load.
    // Avoid showing on settings/billing screens to prevent loops while user is fixing it.
    if (hasAutoShownEmailRequiredRef.current) return;
    if (!currentUser?.userId) return;
    if (isOnSettings || isOnBilling) return;
    // IMPORTANT: billing email lives on Users collection (currentUser).
    if (hasUserEmail(currentUser)) return;
    hasAutoShownEmailRequiredRef.current = true;
    setShowEmailRequiredModal(true);
  }, [currentUser?.userId, currentUser?.email, isOnBilling, isOnSettings]);

  const lastWorkspaceScopeRef = useRef<string>("");
  const resolvedWorkspaceId = useMemo(() => {
    const workspace =
      typeof currentWorkspace === "string"
        ? (() => {
            try {
              return JSON.parse(currentWorkspace || "null");
            } catch {
              return null;
            }
          })()
        : currentWorkspace;
    return String(workspace?._id || "");
  }, [currentWorkspace]);
  const resolvedCurrentUserId = useMemo(() => {
    const rawUserId = currentUser?.userId || (owner as any)?.userId;
    if (!rawUserId) return "";
    if (typeof rawUserId === "string") return rawUserId;
    if (typeof rawUserId === "object" && (rawUserId as any)?._id) {
      return String((rawUserId as any)._id);
    }
    return "";
  }, [currentUser?.userId, (owner as any)?.userId]);

  useEffect(() => {
    if (pathname !== "/") return;
    if (!resolvedCurrentUserId) return;
    if (checkedLinkedinRegistrationConversionUserIdRef.current === resolvedCurrentUserId) return;

    checkedLinkedinRegistrationConversionUserIdRef.current = resolvedCurrentUserId;
    void trackLinkedInRegistrationConversionIfNeeded(resolvedCurrentUserId);
  }, [pathname, resolvedCurrentUserId]);

  useEffect(() => {
    if (!resolvedCurrentUserId) return;
    if (pathname.startsWith("/inbox")) return;
    void refreshInboxUnreadChatsCount({
      userId: resolvedCurrentUserId,
      workspaceId: resolvedWorkspaceId,
      ownerId: owner?._id,
    });
  }, [resolvedCurrentUserId, resolvedWorkspaceId, owner?._id, pathname]);

  useEffect(() => {
    const loadInitialData = async () => {
      const userId = resolvedCurrentUserId;
      if (!userId) return;

      let tempOwner = owner;
      if (!tempOwner) {
        tempOwner = JSON.parse(localStorage.getItem("owner") || "null");
      }

      const normalizeScopeId = (value: unknown): string => {
        if (value == null) return "";
        if (typeof value === "string") return value.trim();
        if (typeof value === "object" && "_id" in (value as object)) {
          return String((value as { _id?: unknown })._id || "").trim();
        }
        return String(value).trim();
      };

      let ownerIdToLoad: string | undefined = tempOwner?._id;
      const storedOwnerUserId = normalizeScopeId(tempOwner?.userId);
      if (storedOwnerUserId && storedOwnerUserId !== userId) {
        ownerIdToLoad = undefined;
      }

      const scopeKey = (id: string) => `${userId}::${id}`;

      try {
        let updatedOwner: any = null;

        if (ownerIdToLoad) {
          if (initializedOwnerIdRef.current === scopeKey(ownerIdToLoad)) return;
          try {
            updatedOwner = await fetchOwner(ownerIdToLoad, userId);
          } catch {
            updatedOwner = null;
          }
        }

        if (!updatedOwner) {
          const accountsResponse = await fetchUserOwnersWithWorkspaces(
            1,
            1,
            "",
            userId
          );
          const firstAccount = accountsResponse?.accounts?.[0];
          if (!firstAccount?._id) return;
          ownerIdToLoad = String(firstAccount._id);
          if (initializedOwnerIdRef.current === scopeKey(ownerIdToLoad)) return;
          updatedOwner = await fetchOwner(ownerIdToLoad, userId);
        }

        if (!updatedOwner?._id) return;

        initializedOwnerIdRef.current = scopeKey(String(updatedOwner._id));
        dispatch(setOwner(updatedOwner));
        dispatch(setCurrentWorkSpace(updatedOwner.workspaces[0]));
        const [lists, templates, campaigns, tags, teams] = await Promise.all([
          fetchLists(
            updatedOwner._id,
            updatedOwner?.workspaces?.[0]?._id,
            updatedOwner?.userId
          ).catch(() => []),
          fetchTemplates(
            updatedOwner._id,
            updatedOwner?.userId,
            updatedOwner?.workspaces?.[0]?._id
          ).catch(() => []),
          fetchCampaigns(
            updatedOwner._id,
            updatedOwner?.userId,
            updatedOwner?.workspaces?.[0]?._id
          ).catch(() => []),
          fetchTags(
            updatedOwner._id,
            updatedOwner?.userId,
            updatedOwner?.workspaces?.[0]?._id
          ).catch(() => []),
          fetchTeamsData(updatedOwner?._id).catch(() => ({ teams: null })),
          dispatch(getAllConnections(updatedOwner._id as string)).catch(() => []),
          dispatch(
            getAllRequests({ ownerId: updatedOwner._id as string, type: "SENT" })
          ).catch(() => []),
          dispatch(getQueuedActions(updatedOwner._id as string)).catch(() => []),
          dispatch(getProfileAnalytics(updatedOwner._id as string)).catch(() => ({})),
        ]);
        dispatch(updateTeam(teams.teams));
        dispatch(setTemplates(templates));
        const nonAllProspectLists = lists.filter(
          (list: List) => list.listName !== "All Prospects"
        );
        dispatch(setLists(nonAllProspectLists));

        const routeListMatch = window.location.pathname.match(/^\/prospects\/([^/]+)(?:\/import)?$/);
        const routeListId = routeListMatch?.[1];
        const preferredListId =
          (routeListId &&
          nonAllProspectLists.some((list: List) => String(list._id) === String(routeListId))
            ? routeListId
            : nonAllProspectLists[0]?._id) || lists[0]?._id;

        if (preferredListId) {
          dispatch(setListId(preferredListId));
        }
        dispatch(setCampaigns(campaigns));
        dispatch(setTags(tags));
        dispatch(setSelectedCampaign("ALL"));
        dispatch(setSelectedTemplate(templates[0]));
        void refreshInboxUnreadChatsCount({
          userId: String(updatedOwner?.userId || userId || ""),
          workspaceId: updatedOwner?.workspaces?.[0]?._id,
          ownerId: updatedOwner._id,
        });
      } catch (error: any) {
        initializedOwnerIdRef.current = null;
        toast.error(error);
      }
    };

    loadInitialData();
  }, [owner?._id, resolvedCurrentUserId]);

  useEffect(() => {
    const loadWorkspaceScopedData = async () => {
      const workspaceId = resolvedWorkspaceId;
      const userId = resolvedCurrentUserId;
      const ownerId = owner?._id || "";
      if (!workspaceId) return;
      const scopeKey = `${ownerId}::${userId}::${workspaceId}`;
      if (lastWorkspaceScopeRef.current === scopeKey) return;
      lastWorkspaceScopeRef.current = scopeKey;

      try {
        const [lists, templates, campaigns, tags] = await Promise.all([
          fetchLists(ownerId, workspaceId, userId).catch(() => []),
          fetchTemplates(ownerId, userId, workspaceId).catch(() => []),
          fetchCampaigns(ownerId, userId, workspaceId).catch(() => []),
          fetchTags(ownerId, userId, workspaceId).catch(() => []),
        ]);

        dispatch(
          setLists(lists.filter((list: List) => list.listName !== "All Prospects"))
        );
        dispatch(setTemplates(templates));
        dispatch(setCampaigns(campaigns));
        dispatch(setTags(tags));

        dispatch(setListId(lists?.[0]?._id || ""));
        dispatch(setSelectedCampaign("ALL"));
        if (templates?.[0]) {
          dispatch(setSelectedTemplate(templates[0]));
        }
      } catch (error) {
        lastWorkspaceScopeRef.current = "";
        console.log(error);
      }
    };

    loadWorkspaceScopedData();
  }, [owner?._id, resolvedWorkspaceId, resolvedCurrentUserId, dispatch]);
  useEffect(() => {
    setHasAutoShownTrialOnSignin(false);
  }, [owner?._id]);

  useEffect(() => {
    if (isLocalBillingBypass) return;
    const shouldAutoOpenOnDashboardSignin =
      SHOW_TRIAL_POPUP &&
      shouldShowTrialPopup(billingSource) &&
      pathname === "/" &&
      !isOnSettings &&
      !hasPaymentFailed(billingSource) &&
      !hasAutoShownTrialOnSignin;

    if (shouldAutoOpenOnDashboardSignin) {
      setShowTrialModal(true);
      setHasAutoShownTrialOnSignin(true);
    }
  }, [owner, pathname, isOnSettings, hasAutoShownTrialOnSignin, isLocalBillingBypass, billingSource]);

  useEffect(() => {
    if (!owner || owner?._id || !isOwnerRequiredArea) {
      hasShownOwnerRequiredToastRef.current = false;
      return;
    }
    if (!hasShownOwnerRequiredToastRef.current) {
      toast.warning(
        "Please link your linkedin account first before using campaigns."
      );
      hasShownOwnerRequiredToastRef.current = true;
    }
    navigate("/teams", { replace: true });
  }, [owner, isOwnerRequiredArea, navigate]);

  useEffect(() => {
    if (!showLinkedInLogin) {
      if (linkedInSocketRef.current) {
        linkedInSocketRef.current.disconnect();
        linkedInSocketRef.current = null;
      }
      return;
    }

    const sock = io(SOCKET_URL, { ...SOCKET_IO_CLIENT_OPTIONS });
    linkedInSocketRef.current = sock;

  sock.on("connectLinkedin_success", (data: any) => {
    console.log(data);
    window.location.reload();
  });
  sock.on("connectLinkedin_error", (data: any) => {
    console.log(data);
  });
  sock.on("connectLinkedin_challenge", (data: any) => { 
    setChallengeIframeUrl(data.url);
    setLinkedinStage("challenge");
    });
  sock.on("connectLinkedin_updateStage", (data: any) => { 
    setLinkedinStage(data.stage);
    });

    return () => {
      sock.disconnect();
      if (linkedInSocketRef.current === sock) {
        linkedInSocketRef.current = null;
    }
    };
  }, [showLinkedInLogin]);

  const handleLinkedInAuth = async () => {
    const sock = linkedInSocketRef.current;
    if (!sock) {
      toast.error("Connection service is not ready. Please retry.");
      return;
    }

    if(linkedinStage === "login"){
      if(sock.connected){
        sock.emit("connectLinkedin_emailPassword", {
            email: linkedinCredentials.email,
            password: linkedinCredentials.password,
            ownerId: owner?._id,
        });
      }else{
        sock.on("connect", ()=>{
          sock.emit("connectLinkedin_emailPassword", {
            email: linkedinCredentials.email,
            password: linkedinCredentials.password,
            ownerId: owner?._id,
          });
        })
      }

      setLinkedinStage("loader")
    }
  }

  const handleUserActions = (data:any) => {
    const sock = linkedInSocketRef.current;
    if (!sock) {
      toast.error("Connection service is not ready. Please retry.");
      return;
    }
    console.log(data);
    sock.emit(data.action, {...data, ownerId: owner?._id});
    setLinkedinStage("loader");
  }

  // UI for LinkedIn login modal
  const LinkedInLoginModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-lg font-bold mb-4">
          {(() => {
            switch (linkedinStage) {
              case "mfa":
                return "Multi-Factor Authentication Required";
              case "success":
                return "LinkedIn Connected!";
              default:
                return "Connect your LinkedIn Account";
            }
          })()}
        </h2>
        {linkedinStage === "login" && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" htmlFor="linkedin-email">
                LinkedIn Email
              </label>
              <input
                id="linkedin-email"
                type="email"
                className="w-full p-2 border border-gray-300 rounded-md"
                value={linkedinCredentials.email || ""}
                onChange={e =>
                  setLinkedinCredentials(prev => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" htmlFor="linkedin-password">
                LinkedIn Password
              </label>
              <input
                id="linkedin-password"
                type="password"
                className="w-full p-2 border border-gray-300 rounded-md"
                value={linkedinCredentials.password || ""}
                onChange={e =>
                  setLinkedinCredentials(prev => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded"
                onClick={handleLinkedInAuth}
                disabled={linkedinLoading}
              >
               Connect
              </button>
            </div>
          </>
        )}
        {linkedinStage === "challenge_linkedinApp" && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Check your LinkedIn app</h3>
          <p className="text-sm text-gray-700 mb-4">
            We sent a notification to your signed in devices. Open your LinkedIn app and tap <span className="font-semibold">Yes</span> to confirm your sign-in attempt.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              onClick={()=>{handleUserActions({action: "click", id:"reset-password-submit-button"})}}
              disabled={linkedinLoading}
            >
              Resend
            </button>
            <button
              type="button"
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded"
              disabled={linkedinLoading}
              onClick={()=>{handleUserActions({action: "click", id:"try-another-way"})}}
            >
              I don't have access to this device
            </button>
          </div>
        </div>
        )}
        {linkedinStage === "challenge_emailVerification" && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Let’s do a quick verification</h3>
          <p className="text-sm text-gray-700 mb-4">
            The login attempt seems suspicious. To finish signing in please enter the verification code we sent to your email address.
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="linkedin-email-code">
            Enter code
          </label>
          <input
            id="linkedin-email-code"
            type="text"
            className="w-full p-2 border border-gray-300 rounded-md mb-4"
            required
          />
          <div className="flex justify-end">
            <button
              type="button"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              disabled={linkedinLoading}
              onClick={()=>{
                const pin = (document.getElementById("linkedin-email-code") as HTMLInputElement)?.value;
                handleUserActions({action: "typeAndSubmit", id: "input__email_verification_pin", pin});
              }}
            >
              Submit
            </button>
          </div>
        </div>
        )}
        {linkedinStage === "challenge" && (
          <div className="mb-4">
            <iframe
              src={chanllengeIframeUrl}
              title="LinkedIn MFA"
              className="w-full h-96 border border-gray-300 rounded-md"
              allow="clipboard-write"
            />
          </div>
        )}
        {linkedinStage === "loader" && (
        <div className="flex flex-col items-center justify-center py-8">
          <svg
            className="animate-spin h-8 w-8 text-blue-600 mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <span className="text-gray-700 text-sm">Connecting to LinkedIn...</span>
        </div>
        )}
      </div>
    </div>
  );

  if (!currentUser) {
    // Route guards in App now redirect unauthenticated users to /login.
    // Keep this as a defensive fallback for stale/mid-hydration state.
    // Legacy "lost page" UI kept below for reference and can be removed later:
    // <div className="flex gap-4 flex-col justify-center items-center h-screen">...</div>
    return <Navigate to="/login" replace />;
  }

  return (
    <main className="flex h-[100dvh] max-h-[100dvh] overflow-y-auto overflow-x-hidden">
      <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Navbar onMenuClick={() => setMobileNavOpen(true)} />
        <div className="min-h-0 min-w-0 flex-1">
          <div className="h-full w-full min-w-0 overflow-visible bg-background-grey">
            <PaymentFailedModal
              show={
                !isLocalBillingBypass &&
                hasPaymentFailed(billingSource) &&
                !isOnBilling &&
                !isOnSettings
              }
              failure={
                (billingSource as any)?.stripePaymentFailure ||
                owner?.stripePaymentFailure ||
                null
              }
            />
            {isLocalBillingBypass ? null : hasPaymentFailed(billingSource) && !isOnBilling && !isOnSettings ? null : (showTrialModal || (SHOW_TRIAL_POPUP && shouldShowTrialPopup(billingSource) && isPlanGatedArea && !isOnSettings)) ? (
              <FreeTrialModal
                isOpen={showTrialModal}
                onClose={() => setShowTrialModal(false)}
                isLaunchFreeBilling={isLaunchFreeBilling}
                pricingCatalog={pricingCatalog}
                prefillEmail={
                  typeof owner?.email === "string"
                    ? owner.email
                    : typeof currentUser?.email === "string"
                    ? currentUser.email
                    : undefined
                }
                onActivateTrial={() => {
                  const userEmail =
                    typeof currentUser?.email === "string" ? currentUser.email.trim() : "";
                  if (!userEmail) {
                    setShowEmailRequiredModal(true);
                    return;
                  }
                  setShowTrialModal(false);
                  navigate("/settings/billing");
                }}
                onScheduleCall={() => {
                  if (!BOOK_DEMO_CALL_URL) return;
                  window.open(BOOK_DEMO_CALL_URL, "_blank");
                  setShowTrialModal(false);
                }}
              />
            ) : null}
            {isLocalBillingBypass
              ? null
              : hasPaymentFailed(billingSource) && !isOnBilling && !isOnSettings
              ? null
              : shouldShowExpiredPlanPopup(billingSource) &&
                isPlanGatedArea &&
                !isOnSettings && (
                  <ExpiredPlanModal
                    show={showExpiredModal}
                    onClose={() => setShowExpiredModal(false)}
                    onResubscribe={async () => {
                      if (!owner?._id) {
                        toast.error("Owner not loaded. Please refresh and try again.");
                        return;
                      }
                      try {
                        setIsResubscribing(true);
                        const resp = await fetch(`${PAYMENT_URL}/resubscribe`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ ownerId: owner._id }),
                        });
                        const data = await resp.json();
                        if (!resp.ok) {
                          const msg =
                            data?.error ||
                            "Unable to start subscription. Please try again.";
                          toast.error(msg);
                          return;
                        }
                        const url = data?.url;
                        if (url) {
                          window.location.href = url;
                        } else {
                          toast.error(
                            "Unable to start subscription. Please try again."
                          );
                        }
                      } catch (e: any) {
                        const msg =
                          e?.message ||
                          "Unable to start subscription. Please try again.";
                        toast.error(msg);
                      } finally {
                        setIsResubscribing(false);
                      }
                    }}
                  />
                )}
            <EmailRequiredModal
              isOpen={showEmailRequiredModal}
              onClose={() => setShowEmailRequiredModal(false)}
              initialEmail={
                typeof currentUser?.email === "string" ? currentUser.email : ""
              }
              onSubmitEmail={async (email) => {
                const resolvedUserId =
                  (typeof currentUser?.userId === "string" && currentUser.userId) ||
                  (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
                  "";
                if (!resolvedUserId) {
                  toast.error("User session not loaded. Please refresh and try again.");
                  return;
                }

                const loader = toast.loading("Saving email...");
                try {
                  const res = await dispatch(
                    updateUserProfile({ userId: resolvedUserId, email }) as any
                  );
                  if ((res as any)?.meta?.requestStatus !== "fulfilled") {
                    throw new Error((res as any)?.payload || "Unable to save email.");
                  }

                  toast.update(loader, {
                    render: "Saved successfully.",
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true,
                  });
                  setShowEmailRequiredModal(false);
                  navigate("/settings/profile");
                } catch (e: any) {
                  toast.update(loader, {
                    render: e?.message || "Unable to save email.",
                    type: "error",
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true,
                  });
                }
              }}
            />

            <div
              className="relative h-full w-full"
              onClickCapture={(e) => {
                const trialGated =
                  SHOW_TRIAL_POPUP &&
                  shouldShowTrialPopup(billingSource) &&
                  isPlanGatedArea &&
                  !isOnSettings;
                const expiredGated =
                  shouldShowExpiredPlanPopup(billingSource) &&
                  isPlanGatedArea &&
                  !isOnSettings;
                const gated =
                  !isLocalBillingBypass &&
                  !(hasPaymentFailed(billingSource) && !isOnBilling && !isOnSettings) &&
                  (trialGated || expiredGated);
                if (!gated) return;

                const target = e.target as HTMLElement | null;
                if (!target) return;

                const actionable = target.closest(
                  'button,a,input,select,textarea,[role="button"],[role="menuitem"],[data-requires-plan="true"],.cursor-pointer'
                ) as HTMLElement | null;
                if (!actionable) return;

                // Let the modal itself work normally
                const insideModal = Boolean(target.closest(".modal-overlay,.modal-container"));
                if (insideModal) return;

                e.preventDefault();
                e.stopPropagation();
                if (trialGated) {
                  setShowTrialModal(true);
                } else if (expiredGated) {
                  setShowExpiredModal(true);
                }
              }}
            >
            <Outlet />
            </div>
          </div>
        </div>
      </div>

      {showLinkedInLogin && (
        <LinkedInLoginModal
        />
      )}

      <ToastContainer {...defaultAppToastContainerProps} />
      <TawkLiveChat />
      {isResubscribing && (
        <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-md shadow-md px-4 py-3">
            <p className="text-sm font-medium text-gray-800">
              Redirecting to checkout, please wait...
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default DefaultLayout;
